import { useState } from 'react'
import { buildEntries } from '../lib/entries.ts'
import { greetingFor } from '../lib/greeting.ts'
import { formatRelative, readRecents } from '../lib/recents.ts'
import { useCatalog } from '../lib/useCatalog.ts'
import { ToolCard } from './ToolCard.tsx'
import { ToolCardSkeleton } from './ToolCardSkeleton.tsx'

const FULL_DATE = new Intl.DateTimeFormat('en-AU', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})

/**
 * A section title with a rule running off it to the edge of the column.
 *
 * The title is set in the display face because it names a thing. It used to be
 * set in the mono caption register at 0.68rem, which is the same size as the
 * timestamp on a card, so a section heading carried no more weight than a piece
 * of card metadata: that is what read as thin.
 *
 * A count sat at the far end of the rule for a while. It came out as a bare
 * digit stranded at the right of a long rule, which said less than the cards
 * sitting under it already do.
 */
function SectionHead({ title }: { title: string }) {
  return (
    <h2 className="section-head">
      <span className="section-head__title">{title}</span>
    </h2>
  )
}

/**
 * Landing page for a signed-in user: greeting, the tools they've used recently,
 * then the full catalog.
 *
 * `name` is the same placeholder TopBar uses — Phase 0 has no per-account
 * identity, so everyone is "Admin" until auth lands in Phase 2.
 */
export function Dashboard({ name = 'Admin' }: { name?: string }) {
  const { items, topics, error } = useCatalog()

  // Read once on mount, not on every render: the list is written by ToolPage
  // and DocumentPage on other routes, so re-reading here would only ever
  // return the same value.
  const [recents] = useState(readRecents)

  const now = new Date()

  // Both halves have to be in hand before a card can be right: entries built
  // from artefacts alone would show a topic's documents as loose cards.
  const loaded = items !== null && topics !== null

  // A topic collapses to one card — its policy, procedures and forms are read
  // as a single bundle, not 13 near-identical rows. Shared with DepartmentPage
  // so the two listings (and HubLanding's counts) can't disagree about shape.
  const entries = buildEntries(items ?? [], topics ?? [])

  return (
    <main className="dash">
      <h1 className="dash__greet">
        {greetingFor(now)}, {name}!
      </h1>

      {/* The date under a rule: the masthead of an Astute document, which opens
          the same way. The rule is what separates the greeting from the work
          below it, so it stays even though the strip holds one cell. */}
      <div className="record">
        <span className="record__cell">{FULL_DATE.format(now)}</span>
      </div>

      <section className="dash__section">
        <SectionHead title="Pick up where you left off" />
        {recents.length === 0 ? (
          <p className="empty">No tools used yet. Open one below and it'll show up here.</p>
        ) : (
          <div className="catalog__grid">
            {recents.map((r) => (
              <ToolCard
                key={r.id}
                id={r.id}
                to={r.to}
                department={r.department}
                title={r.title}
                isDraft={r.isDraft}
                footer={<p className="card__when">{formatRelative(r.lastUsedAt, now)}</p>}
              />
            ))}
          </div>
        )}
      </section>

      <section className="dash__section">
        {/* Not "All tools": a topic collapses to one card here, and a policy
            bundle is not a tool. */}
        <SectionHead title="Everything in the office" />
        {error && <p className="notice notice--error">{error}</p>}
        {/* Only claim an empty office once the catalog has actually landed,
            or this flashes on every cold load before the cards arrive. */}
        {!loaded ? (
          !error && <ToolCardSkeleton />
        ) : entries.length === 0 ? (
          <p className="empty">No tools are available yet.</p>
        ) : (
          <div className="catalog__grid">
            {entries.map((e) => (
              <ToolCard
                key={e.id}
                id={e.id}
                to={e.to}
                department={e.department}
                title={e.title}
                isDraft={e.isDraft}
                meta={e.meta}
              />
            ))}
          </div>
        )}
      </section>
    </main>
  )
}
